import { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  TextField,
  Typography,
} from "@material-ui/core";
import Menu from "../menu/menu";
import { api } from "../../services/apiService";
import { CONSTANTS } from "../../constants/app-constant";
import QuestionTagsSection from "./question-tags-section";
import "./mentorships.scss";

const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

/**
 * Flag-gated tutor<->student mentorships (phase 1: view-only follow +
 * request/accept). Self-contained — holds its own state and calls the
 * apiService directly (the /mentorships endpoints per
 * docs/qlearn-tutor-student-sharing-design.md). Inert behind
 * CONSTANTS.mentorshipsEnabled until the backend lands; the list call 404s
 * gracefully meanwhile.
 */
export default function MentorshipsPage() {
  const history = useHistory();
  const role = typeof window !== "undefined" ? window.localStorage.getItem("role") : null;
  const isTutor = role === "ROLE_TUTOR";
  const myParty = isTutor ? "TUTOR" : "STUDENT";
  // Tutors follow students; students ask a tutor to follow them.
  const counterpartLabel = isTutor ? "student" : "tutor";

  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [busyId, setBusyId] = useState("");
  // T4: per-student OPEN-tag counts for the roster (inline count, Slice E/F
  // precedent). null until (and unless) the one tutor-side list call succeeds.
  const [openTagCounts, setOpenTagCounts] = useState(null);

  useEffect(() => {
    if (!isTutor || !CONSTANTS.mentorshipTagsEnabled) return undefined;
    let cancelled = false;
    (async () => {
      try {
        // Server-scoped to tags addressed to the caller over LIVE links
        // (§10.3-R1/R2). Counts are a nicety: a dark 404 — the same single
        // seam every mentorship call shares — or any failure just leaves the
        // roster without counts. No error UI, no retry, no detail.
        const res = await api.listQuestionTags({ status: "OPEN" });
        if (cancelled || !Array.isArray(res)) return;
        const counts = {};
        res.forEach((t) => {
          if (t?.studentUserId) counts[t.studentUserId] = (counts[t.studentUserId] || 0) + 1;
        });
        setOpenTagCounts(counts);
      } catch (e) {
        // stay silent — the roster renders identically without counts
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isTutor]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.listMentorships();
      const status = res?.errStatus || res?.status;
      if (status === 404) {
        // Backend not enabled yet — treat as an empty list, not an error.
        setList([]);
        return;
      }
      // Only treat a real array (or {mentorships:[...]}) as data. doGetCall
      // resolves {status, errMsg} for any non-200 (even a bodyless one) and a
      // raw Error only on a network drop — both must surface as an error here,
      // not an empty list.
      if (Array.isArray(res)) {
        setList(res);
      } else if (Array.isArray(res?.mentorships)) {
        setList(res.mentorships);
      } else {
        setError("Couldn't load your mentorships. Please try again.");
        setList(null);
      }
    } catch (e) {
      setError("Couldn't load your mentorships. Please try again.");
      setList(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const respond = async (id, op) => {
    setBusyId(id);
    setError("");
    let failed = false;
    try {
      // doUpdateCall never rejects — it RESOLVES {errStatus, errMsg} on failure,
      // so we must inspect the resolved value, not rely on catch.
      const res = await api.respondToMentorship(id, op);
      failed = !!(res?.errStatus || res?.errMsg);
      if (!failed && res?.id) {
        // Consume the updated DTO (the backend contract pins a non-empty body):
        // patch the row in place so the new status shows immediately; the
        // refetch below stays authoritative for everything else.
        setList((cur) => (Array.isArray(cur) ? cur.map((m) => (m?.id === res.id ? { ...m, ...res } : m)) : cur));
      }
    } catch (e) {
      failed = true;
    }
    // Reload first (load() clears the error banner), then surface the failure so
    // it isn't wiped by the refetch — a silent failure would just show stale state.
    // Keep the row busy across the refetch so a fast second click can't fire a
    // duplicate mutation against the stale list.
    await load();
    setBusyId("");
    if (failed) setError("Couldn't update that request. Please try again.");
  };

  const canSend = isValidEmail(email.trim()) && !submitting;

  const sendRequest = async () => {
    if (!canSend) return;
    setSubmitting(true);
    setError("");
    try {
      const res = await api.requestMentorship({
        counterpartEmail: email.trim(),
        message: message.trim() || undefined,
      });
      const status = res?.errStatus || res?.status;
      if (status === 404) {
        // Uniform server 404 (QLS #526): unknown email AND non-tutor answer
        // identically, so the copy must cover both without distinguishing them.
        setError("No tutor found for that email.");
        return;
      }
      if (res?.errStatus || res?.errMsg) {
        setError("Couldn't send that request. Check the email and try again.");
        return;
      }
      setEmail("");
      setMessage("");
      load();
    } catch (e) {
      setError("Couldn't send that request. Check the email and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const items = list || [];
  // Incoming = a PENDING the OTHER party sent me (I accept/decline). A missing
  // initiatedBy also lands here (actionable) rather than vanishing from every bucket.
  const incoming = items.filter((m) => m.status === "PENDING" && m.initiatedBy !== myParty);
  const outgoing = items.filter((m) => m.status === "PENDING" && m.initiatedBy === myParty);
  const active = items.filter((m) => m.status === "ACTIVE");

  // MentorshipDto is FLAT (counterpartDisplayName/counterpartEmail/counterpartUserId
  // + tutorUserId/studentUserId) — there is no nested counterpart object.
  const name = (m) => m.counterpartDisplayName || m.counterpartEmail || `A ${counterpartLabel}`;

  return (
    <Box className="body-cont-over mentorships-page">
      <Menu />
      <div className="mtr-shell">
        <div className="mtr-header">
          <div className="mtr-title-row">
            <Typography variant="h5">{isTutor ? "My students" : "My tutors"}</Typography>
            <Chip size="small" color="primary" label="BETA" className="mtr-beta" />
          </div>
          <Typography variant="body2" className="mtr-sub">
            {isTutor
              ? "Follow a student's quiz progress (view-only) once they accept. Either of you can start the request; either can stop sharing at any time."
              : "Ask a tutor to follow your quiz progress (view-only). They see your scores once you both agree, and you can stop sharing at any time."}
          </Typography>
        </div>

        {/* Request form */}
        <div className="mtr-card mtr-request">
          <Typography variant="subtitle1" className="mtr-card-title">
            Invite a {counterpartLabel}
          </Typography>
          <div className="mtr-request-row">
            <TextField
              className="mtr-field"
              size="small"
              variant="outlined"
              type="email"
              placeholder={`${counterpartLabel}@example.com`}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              inputProps={{ "aria-label": `${counterpartLabel} email`, "data-testid": "mtr-email" }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={sendRequest}
              disabled={!canSend}
              data-testid="mtr-send"
            >
              {submitting ? <CircularProgress size={20} color="inherit" /> : "Send request"}
            </Button>
          </div>
          <TextField
            className="mtr-message"
            size="small"
            variant="outlined"
            multiline
            maxRows={3}
            placeholder="Add a note (optional)"
            value={message}
            onChange={(e) => setMessage(e.target.value.slice(0, 300))}
            inputProps={{ maxLength: 300, "aria-label": "Optional message" }}
          />
        </div>

        {error && (
          <Typography className="mtr-error" variant="body2" data-testid="mtr-error">
            {error}
          </Typography>
        )}

        {loading && !list && (
          <div className="mtr-loading" data-testid="mtr-loading">
            <CircularProgress size={22} />
            <Typography variant="caption" className="mtr-sub">Loading…</Typography>
          </div>
        )}

        {/* Incoming requests to accept/decline */}
        {incoming.length > 0 && (
          <div className="mtr-section" data-testid="mtr-incoming">
            <Typography variant="subtitle2" className="mtr-section-title">
              Requests for you
            </Typography>
            {incoming.map((m) => (
              <div className="mtr-card mtr-item" key={m.id}>
                <div className="mtr-item-main">
                  <Typography className="mtr-name">{name(m)}</Typography>
                  {m.requestMessage && (
                    <Typography variant="body2" className="mtr-msg">“{m.requestMessage}”</Typography>
                  )}
                  <Typography variant="caption" className="mtr-sub">
                    wants to {isTutor ? "be followed by you" : "follow your progress"}
                  </Typography>
                </div>
                <div className="mtr-item-actions">
                  <Button
                    className="mtr-btn mtr-btn--accept"
                    variant="contained"
                    size="small"
                    disabled={busyId === m.id}
                    onClick={() => respond(m.id, "accept")}
                  >
                    Accept
                  </Button>
                  <Button
                    className="mtr-btn"
                    variant="outlined"
                    size="small"
                    disabled={busyId === m.id}
                    onClick={() => respond(m.id, "decline")}
                  >
                    Decline
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Active mentorships */}
        <div className="mtr-section" data-testid="mtr-active">
          <Typography variant="subtitle2" className="mtr-section-title">
            {isTutor ? "Students you follow" : "Tutors following you"}
          </Typography>
          {active.length === 0 && !loading && !error ? (
            <Typography variant="body2" className="mtr-empty" data-testid="mtr-active-empty">
              No active connections yet — invite a {counterpartLabel} above to get started.
            </Typography>
          ) : (
            active.map((m) => {
              // Tutors can open a linked student's progress view.
              const studentId = m.studentUserId;
              const openProgress =
                isTutor && studentId ? () => history.push(`/mentorships/students/${studentId}`) : null;
              // T4: OPEN tags this student has waiting for the caller.
              const tagCount = (studentId && openTagCounts?.[studentId]) || 0;
              return (
              <div
                className={`mtr-card mtr-item${openProgress ? " mtr-item--link" : ""}`}
                key={m.id}
              >
                <div
                  className="mtr-item-main"
                  role={openProgress ? "button" : undefined}
                  tabIndex={openProgress ? 0 : undefined}
                  onClick={openProgress || undefined}
                  onKeyDown={openProgress ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openProgress(); } } : undefined}
                >
                  <div className="tag-row-head">
                    <Typography className="mtr-name">{name(m)}</Typography>
                    {tagCount > 0 && (
                      <Chip
                        size="small"
                        className="tag-chip tag-chip--open"
                        label={tagCount === 1 ? "1 tagged question" : `${tagCount} tagged questions`}
                        data-testid="mtr-tag-count"
                      />
                    )}
                  </div>
                  <Typography variant="caption" className="mtr-sub">
                    {openProgress ? "View-only · tap to see progress" : "View-only · connected"}
                  </Typography>
                </div>
                <Button
                  className="mtr-btn"
                  variant="outlined"
                  size="small"
                  disabled={busyId === m.id}
                  onClick={() => respond(m.id, "revoke")}
                >
                  Stop sharing
                </Button>
              </div>
            );
            })
          )}
        </div>

        {/* Student→tutor question tags (T3, sub-flag-gated; the tutor surface
            is the roster counts above + the section on the student page, T4).
            Section renders nothing until the student has tags. */}
        {!isTutor && CONSTANTS.mentorshipTagsEnabled && <QuestionTagsSection />}

        {/* Sent, awaiting response */}
        {outgoing.length > 0 && (
          <div className="mtr-section" data-testid="mtr-outgoing">
            <Typography variant="subtitle2" className="mtr-section-title">
              Sent · awaiting response
            </Typography>
            {outgoing.map((m) => (
              <div className="mtr-card mtr-item" key={m.id}>
                <div className="mtr-item-main">
                  <Typography className="mtr-name">{name(m)}</Typography>
                  <Typography variant="caption" className="mtr-sub">Waiting for them to accept</Typography>
                </div>
                <Button
                  className="mtr-btn"
                  variant="outlined"
                  size="small"
                  disabled={busyId === m.id}
                  onClick={() => respond(m.id, "revoke")}
                >
                  Cancel
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </Box>
  );
}
